import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Alert, AppBar, Avatar, Box, Button, Divider, Drawer, IconButton,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Popover,
  Toolbar, Typography, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountBalanceWalletOutlined, ArrowBack, Business, BusinessOutlined, ChevronRight,
  Close, ExpandMore, HelpOutline, HistoryOutlined,
  Inventory2Outlined, Logout, MenuRounded, People, PeopleAltOutlined,
  LanguageOutlined, PaletteOutlined, PersonOutline, PointOfSaleOutlined,
  ReceiptLongOutlined, SpaceDashboardOutlined, WarningAmber, AssessmentOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { Language, useLanguage } from '../../contexts/LanguageContext';
import { useTheme as useAppearance } from '../../contexts/ThemeContext';
import { ThemeMode } from '../../theme/foundations';
import { NAV_PAGE_ACCENTS, PAGE_ACCENTS } from '../../theme/pageAccents';
import { NAVIGATION_ITEMS } from '../../utils/constants';
import { returnFromCustomerDetail } from '../../utils/customerNavigation';
import { getPersonInitials } from '../../utils/initials';
import HelpSettings from './HelpSettings';
import PageIconTile from './PageIconTile';
import BrandLockup from '../brand/BrandLockup';
import { MobileTopBarAction, MobileTopBarActionsContext } from './MobileTopBarActionsContext';
import { useIsMobileShell } from './useIsMobileShell';

const drawerWidth = 248;
const dailyOrder = ['/dashboard', '/reports', '/customers', '/sales', '/debts', '/items', '/expenses'];
const administrationPaths = ['/companies', '/users'];
const supportPaths = ['/help'];
const superAdminPrimaryPaths = ['/dashboard'];
const superAdminSecondaryPaths = ['/reports'];
const mobileTitleKeys: Record<string, string> = {
  dashboard: 'nav.dashboard',
  reports: 'nav.reports',
  companies: 'nav.companies',
  users: 'nav.users',
  customers: 'nav.customers',
  items: 'nav.items',
  sales: 'nav.sales',
  debts: 'nav.debts',
  expenses: 'nav.expenses',
  profile: 'nav.profile',
  help: 'nav.help',
  brand: 'brand.pageTitle',
};

const iconMap = {
  dashboard: <SpaceDashboardOutlined />,
  reports: <AssessmentOutlined />,
  business: <Business />,
  people: <People />,
  person: <PeopleAltOutlined />,
  inventory: <Inventory2Outlined />,
  shopping_cart: <PointOfSaleOutlined />,
  history: <HistoryOutlined />,
  receipt: <AccountBalanceWalletOutlined />,
  expense: <ReceiptLongOutlined />,
  help: <HelpOutline />,
};

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [helpSettingsOpen, setHelpSettingsOpen] = useState(false);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [pageActions, setPageActions] = useState<MobileTopBarAction[]>([]);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const { user, logout, isReadOnly } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { mode, setMode } = useAppearance();
  const theme = useTheme();
  const isMobileShell = useIsMobileShell();
  const isDesktop = !isMobileShell;
  const navigate = useNavigate();
  const location = useLocation();
  const showTopBarHelp = !isDesktop;
  const hasPageHelp = pageActions.some(action => action.kind === 'help');
  const showMobilePageMenu = location.pathname !== '/help' || pageActions.length > 0;
  const isFullBleedMobile = isMobileShell && (['/customers', '/companies', '/debts', '/sales', '/items', '/expenses', '/users', '/profile'].includes(location.pathname) || location.pathname.startsWith('/customers/'));
  const isCustomerDetail = /^\/customers\/[^/]+\/?$/.test(location.pathname);
  const routeSegment = location.pathname.split('/').filter(Boolean)[0] || 'dashboard';
  const customerDetailName = location.pathname.startsWith('/customers/')
    ? (location.state as { customerName?: string } | null)?.customerName
    : undefined;
  const mobilePageTitle = customerDetailName || (mobileTitleKeys[routeSegment] ? t(mobileTitleKeys[routeSegment]) : 'Veresiye');
  const accountOpen = Boolean(anchorEl);
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || user?.email || t('layout.account');
  const initials = getPersonInitials(user?.firstName, user?.lastName, language, name);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const companyName = user?.companyName || user?.company?.name || t('layout.workspace');
  const navigationItems = (user ? NAVIGATION_ITEMS[user.role] || [] : []).map(item => ({
    ...item,
    label: t(`nav.${item.path.slice(1)}`),
  }));
  const dailyItems = navigationItems
    .filter(item => !administrationPaths.includes(item.path) && !supportPaths.includes(item.path)
      && (!isSuperAdmin || superAdminPrimaryPaths.includes(item.path)))
    .sort((a, b) => {
      const rank = (path: string) => dailyOrder.includes(path) ? dailyOrder.indexOf(path) : dailyOrder.length;
      return rank(a.path) - rank(b.path);
    });
  const administrationItems = navigationItems.filter(item => administrationPaths.includes(item.path));
  const reportingItems = isSuperAdmin
    ? navigationItems.filter(item => superAdminSecondaryPaths.includes(item.path))
    : [];
  const supportItems = navigationItems.filter(item => supportPaths.includes(item.path));
  const navigationShellBaseColor = mode === 'dark' ? '#10261e' : '#173a2f';
  const navigationShellBackground = mode === 'dark'
    ? 'radial-gradient(ellipse at 4% 0%, #acc29b15, transparent 60%), linear-gradient(160deg, #233e31, #10261e)'
    : 'radial-gradient(ellipse at 4% 0%, #acc29b15, transparent 60%), linear-gradient(160deg, #2c5748, #173a2f)';

  const registerPageAction = useCallback((action: MobileTopBarAction) => {
    setPageActions(current => [...current.filter(existing => existing.id !== action.id), action]);
    return () => setPageActions(current => current.filter(existing => existing.id !== action.id));
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setAnchorEl(null);
    setMobileMenuAnchorEl(null);
  }, [location.pathname, isDesktop]);

  const closeAccount = () => setAnchorEl(null);
  const handleLogout = async () => {
    closeAccount();
    setMobileOpen(false);
    await logout();
    navigate('/login');
  };

  const renderItems = (items: typeof navigationItems) => (
    <List disablePadding>
      {items.map(item => {
        const selected = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
        const accentName = NAV_PAGE_ACCENTS[item.path] ?? 'dashboard';
        const accent = PAGE_ACCENTS[accentName].dark;
        return (
          <ListItem key={item.path} disablePadding sx={{ my: 0.375 }}>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={selected}
              aria-current={selected ? 'page' : undefined}
              onClick={() => setMobileOpen(false)}
              sx={{
                borderRadius: '6px', px: 1.5, gap: 1.5, color: '#F5F7EF',
                '&:not(.Mui-selected):hover': {
                  color: '#FFFFFF',
                  bgcolor: 'rgba(255, 255, 255, .12)',
                },
                '&.Mui-selected': {
                  color: '#F5F7EF',
                  bgcolor: alpha(accent, 0.18),
                  '&:hover': { bgcolor: alpha(accent, 0.26) },
                  '&::before': {
                    content: '""', position: 'absolute', left: 0,
                    width: 3, height: 24, bgcolor: accent, borderRadius: 1,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <PageIconTile
                  icon={iconMap[item.icon as keyof typeof iconMap]}
                  accent={accentName}
                  size={36}
                  iconSize={20}
                  onDarkSurface
                />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{ primary: { fontSize: 16, fontWeight: selected ? 600 : 400 } }}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  const groupLabel = (label: string) => (
    <Typography sx={{ px: 1.5, mb: 1.25, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#D1E1D3' }}>
      {label}
    </Typography>
  );

  const renderDrawer = (mobile: boolean) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', px: 2.5, color: '#F5F7EF' }}>
      <Box sx={{ minHeight: mobile ? 72 : 88, px: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <BrandLockup markSize={42} fontSize={25} onDarkSurface gap={.75} />
        {mobile && (
          <IconButton aria-label={t('layout.closeMenu')} onClick={() => setMobileOpen(false)} sx={{ mr: -1.5 }}>
            <Close />
          </IconButton>
        )}
      </Box>
      {mobile && <ListItemButton component={Link} to="/profile" onClick={() => setMobileOpen(false)} aria-label={t('layout.myProfile')} aria-current={location.pathname === '/profile' ? 'page' : undefined} sx={{ borderRadius: 1, gap: 1.5, mb: 2, px: 1.5, py: 1.25, bgcolor: '#ffffff12', color: '#F5F7EF', '&:hover': { bgcolor: '#ffffff20' } }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: '#ffffff1f', color: '#F5F7EF', border: '1px solid #ffffff24', fontSize: 13, fontWeight: 700 }}>{initials}</Avatar>
        <ListItemText primary={name} secondary={t('layout.myProfile')} slotProps={{ primary: { noWrap: true, fontSize: 15, fontWeight: 600 }, secondary: { noWrap: true, fontSize: 12, sx: { color: '#D1E1D3' } } }} />
        <ChevronRight fontSize="small" />
      </ListItemButton>}
      {mobile && !isSuperAdmin && <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: '8px 12px', mb: 2 }}>
        <BusinessOutlined sx={{ mt: 0.25, flexShrink: 0, fontSize: 20, color: '#D1E1D3' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography title={companyName} sx={{ fontSize: 14, lineHeight: 1.25, fontWeight: 600, overflowWrap: 'anywhere' }}>{companyName}</Typography>
        </Box>
      </Box>}
      <Box component="nav" aria-label={t('layout.mainNavigation')}>
        {groupLabel(t('layout.dailyWork'))}
        {renderItems(dailyItems)}
        {administrationItems.length > 0 && (
          <Box sx={{ mt: 3.5 }}>
            {groupLabel(t('layout.administration'))}
            {renderItems(administrationItems)}
          </Box>
        )}
        {reportingItems.length > 0 && (
          <Box sx={{ mt: 3.5 }}>
            {groupLabel(t('layout.reporting'))}
            {renderItems(reportingItems)}
          </Box>
        )}
      </Box>
      <Box component="nav" aria-label={t('layout.supportNavigation')} sx={{ mt: 'auto', pt: 2.5, pb: 2.75 }}>
        <Divider sx={{ mb: 2 }} />
        {renderItems(supportItems)}
        {mobile && <>
          <Divider sx={{ my: 2 }} />
          <ListItemButton onClick={() => { setMobileOpen(false); setHelpSettingsOpen(true); }} sx={{ borderRadius: 1, px: 1.5, color: '#F5F7EF' }}>
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}><HelpOutline /></ListItemIcon>
            <ListItemText primary={t('help.helpSettings')} />
          </ListItemButton>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1, px: 1.5, color: '#F5F7EF' }}>
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}><Logout /></ListItemIcon>
            <ListItemText primary={t('layout.signOut')} />
          </ListItemButton>
        </>}
        <Typography sx={{ px: 1.5, pt: 2, fontSize: 12, color: '#D1E1D3' }}>{t('layout.tagline')}</Typography>
      </Box>
    </Box>
  );

  const preferenceSelect = {
    font: 'inherit', fontSize: 14, color: 'text.primary', bgcolor: 'background.paper',
    border: '1px solid', borderColor: 'divider', borderRadius: '10px',
    minHeight: 42, width: { xs: 120, sm: 140 }, px: 1.25, cursor: 'pointer',
    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
  };
  const accountRow = {
    width: '100%', minHeight: 48, px: 1.25, justifyContent: 'flex-start', gap: 1.25,
    fontSize: 14, fontWeight: 600, color: 'text.primary', borderRadius: 1.5,
    '&:hover': { bgcolor: 'action.hover' },
  };
  const preferenceRow = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 1, minHeight: 56, px: { xs: 0.75, sm: 1.25 }, borderRadius: 1.5, bgcolor: 'action.hover',
  };

  return (
    <MobileTopBarActionsContext.Provider value={showTopBarHelp ? registerPageAction : null}>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Button component="a" href="#main-content" sx={{
        position: 'fixed', top: -80, left: 16, zIndex: theme.zIndex.modal + 1,
        bgcolor: 'background.paper', '&:focus': { top: 12 },
      }}>{t('layout.skipContent')}</Button>
      <AppBar position="fixed" elevation={0} sx={{
        top: 0,
        left: { md: drawerWidth },
        width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
        ml: 0,
        color: '#F5F7EF',
        background: navigationShellBackground,
        border: 0,
        borderBottom: { xs: '1px solid rgba(255, 255, 255, .12)', md: 0 },
        borderRadius: 0,
        boxShadow: { xs: '0 4px 18px rgba(19, 47, 33, .08)', md: 'none' },
      }}>
        <Toolbar sx={{ minHeight: { xs: '60px !important', md: '72px !important' }, px: { xs: '12px !important', md: '24px !important' }, display: isDesktop ? 'flex' : 'grid', gridTemplateColumns: isDesktop ? undefined : 'minmax(max-content, 1fr) minmax(0, max-content) minmax(max-content, 1fr)', gap: isDesktop ? 1.5 : 1, justifyContent: isDesktop ? 'space-between' : undefined }}>
          {!isDesktop ? (
            <>
              <IconButton
                onClick={isCustomerDetail ? () => returnFromCustomerDetail(navigate, location.state) : () => setMobileOpen(true)}
                aria-label={t(isCustomerDetail ? 'layout.back' : 'layout.openMenu')}
                aria-expanded={isCustomerDetail ? undefined : mobileOpen}
                aria-controls={!isCustomerDetail && mobileOpen ? 'mobile-navigation' : undefined}
                sx={{ color: '#F5F7EF', width: 44, height: 44, justifySelf: 'start', '&:hover': { bgcolor: 'rgba(255,255,255,.1)' } }}
              >
                {isCustomerDetail ? <ArrowBack /> : <MenuRounded />}
              </IconButton>
              <Typography component="h1" noWrap title={mobilePageTitle} sx={{ minWidth: 0, maxWidth: '100%', justifySelf: 'center', textAlign: 'center', fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{mobilePageTitle}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifySelf: 'end', minWidth: 44, height: 44 }}>
                {showMobilePageMenu && <IconButton
                  ref={mobileMenuButtonRef}
                  id="mobile-page-actions-button"
                  aria-label={t('layout.pageActions')}
                  aria-haspopup="menu"
                  aria-controls={mobileMenuAnchorEl ? 'mobile-page-actions-menu' : undefined}
                  aria-expanded={Boolean(mobileMenuAnchorEl)}
                  onClick={event => setMobileMenuAnchorEl(event.currentTarget)}
                  sx={{
                    width: 44,
                    height: 44,
                    color: '#F5F7EF',
                    border: '1px solid rgba(255,255,255,.35)',
                    borderRadius: mobileMenuAnchorEl ? '22px 22px 0 0' : '50%',
                    bgcolor: mobileMenuAnchorEl ? 'rgba(255,255,255,.12)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(255,255,255,.12)' },
                  }}
                ><ExpandMore sx={{ transform: mobileMenuAnchorEl ? 'rotate(180deg)' : undefined, transition: 'transform 160ms ease' }} /></IconButton>}
              </Box>
            </>
          ) : isSuperAdmin ? null : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Avatar variant="rounded" sx={{ width: 42, height: 42, borderRadius: '11px', bgcolor: '#ffffff18', color: '#F5F7EF', border: '1px solid #ffffff24' }}><BusinessOutlined /></Avatar>
              <Typography noWrap title={companyName} sx={{ fontSize: 21, lineHeight: 1.2, fontWeight: 600, letterSpacing: '-0.025em' }}>{companyName}</Typography>
            </Box>
          )}
          {isDesktop && (
            <Button
              id="account-preferences-button"
              aria-label={t('layout.account')}
              aria-haspopup="dialog"
              aria-controls={accountOpen ? 'account-preferences' : undefined}
              aria-expanded={accountOpen}
              onClick={event => setAnchorEl(event.currentTarget)}
              sx={{ color: '#F5F7EF', px: 1, minWidth: 0, minHeight: 44, gap: 1.5, borderRadius: 1, ml: 'auto', flexShrink: 0, '&:hover': { bgcolor: 'rgba(255,255,255,.1)' } }}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: '#ffffff18', color: '#F5F7EF', border: '1px solid #ffffff24', fontSize: 12, fontWeight: 600 }}>{initials}</Avatar>
              <Box sx={{ textAlign: 'left', maxWidth: 200 }}>
                <Typography noWrap title={name} sx={{ fontSize: 14, fontWeight: 600, color: '#F5F7EF' }}>{name}</Typography>
                <Typography sx={{ fontSize: 12, color: '#D1E1D3' }}>{user ? t(`role.${user.role}`) : ''}</Typography>
              </Box>
              <ExpandMore sx={{ fontSize: 18 }} />
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Menu
        id="mobile-page-actions-menu"
        anchorEl={mobileMenuAnchorEl}
        open={Boolean(mobileMenuAnchorEl)}
        onClose={() => setMobileMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={0}
        MenuListProps={{ 'aria-labelledby': 'mobile-page-actions-button' }}
        slotProps={{ paper: { sx: {
          minWidth: 224,
          maxWidth: 'calc(100vw - 32px)',
          mt: '-1px',
          borderRadius: '14px 0 14px 14px',
          overflow: 'hidden',
        } } }}
      >
        {[...pageActions].sort((a, b) => (a.order ?? 50) - (b.order ?? 50)).map(action => (
          <MenuItem key={action.id} onClick={() => {
            setMobileMenuAnchorEl(null);
            if (mobileMenuButtonRef.current) action.onSelect(mobileMenuButtonRef.current);
          }} sx={{ minHeight: 48 }}>
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
        {!hasPageHelp && location.pathname !== '/help' && <MenuItem component={Link} to="/help" onClick={() => setMobileMenuAnchorEl(null)} sx={{ minHeight: 48 }}>
          <ListItemIcon><HelpOutline fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('nav.help')} />
        </MenuItem>}
      </Menu>
      <Box sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {!isDesktop && <Drawer
          variant="temporary"
          open={!isDesktop && mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          slotProps={{ paper: { id: 'mobile-navigation', role: 'dialog', 'aria-modal': true, 'aria-label': t('layout.mainNavigation') } }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': {
            width: 296, maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box',
            color: '#F5F7EF', backgroundColor: navigationShellBaseColor,
            backgroundImage: navigationShellBackground, overflowY: 'auto',
            scrollbarWidth: 'none', msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none', width: 0, height: 0 },
          } }}
        >{renderDrawer(true)}</Drawer>}
        {isDesktop && <Drawer
          variant="permanent"
          open
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': {
            width: drawerWidth, boxSizing: 'border-box', borderRight: 0,
            color: '#F5F7EF', backgroundColor: navigationShellBaseColor,
            backgroundImage: navigationShellBackground, overflowY: 'auto',
          } }}
        >{renderDrawer(false)}</Drawer>}
      </Box>
      <Box component="main" id="main-content" tabIndex={-1} sx={{
        flexGrow: 1, minWidth: 0, width: { md: `calc(100% - ${drawerWidth}px)` },
        p: { xs: isFullBleedMobile ? 0 : '24px 20px', md: '24px 44px' },
        bgcolor: isFullBleedMobile ? 'background.paper' : 'transparent',
      }}>
        <Box aria-hidden="true" sx={{ height: { xs: 60, md: 72 } }} />
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          {isReadOnly && <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>{t('subscription.readOnlyBanner')}</Alert>}
          <Outlet />
        </Box>
      </Box>
      <Popover
        open={accountOpen}
        anchorEl={anchorEl}
        onClose={closeAccount}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: {
          id: 'account-preferences', role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'account-preferences-title',
          sx: {
            width: 328, maxWidth: 'calc(100vw - 32px)', p: 2, mt: 1,
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            bgcolor: 'background.paper', backgroundImage: 'none',
            boxShadow: '0 18px 48px rgba(20, 32, 26, 0.16)',
          },
        } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 1.25, mb: 0.75 }}>
          <Typography id="account-preferences-title" sx={{ flex: 1, fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
            {t('layout.accountPreferences')}
          </Typography>
          <IconButton aria-label={t('common.close')} onClick={closeAccount} sx={{ width: 40, height: 40 }}><Close /></IconButton>
        </Box>
        <Box sx={{ display: 'grid', gap: 0.75 }}>
          <Button component={Link} to="/profile" onClick={closeAccount} startIcon={<PersonOutline />} endIcon={<ChevronRight />} sx={accountRow}>
            {t('layout.myProfile')}
          </Button>
          <Box sx={preferenceRow}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <LanguageOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Box component="label" htmlFor="app-language" sx={{ fontSize: 14, fontWeight: 600 }}>{t('layout.language')}</Box>
            </Box>
            <Box component="select" id="app-language" value={language} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setLanguage(event.target.value as Language)} sx={preferenceSelect}>
              <option value="tr">Türkçe</option><option value="en">English</option>
            </Box>
          </Box>
          <Box sx={preferenceRow}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <PaletteOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Box component="label" htmlFor="app-theme" sx={{ fontSize: 14, fontWeight: 600 }}>{t('layout.appearance')}</Box>
            </Box>
            <Box component="select" id="app-theme" value={mode} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setMode(event.target.value as ThemeMode)} sx={preferenceSelect}>
              <option value="light">{t('theme.light')}</option><option value="dark">{t('theme.dark')}</option>
            </Box>
          </Box>
          <Button onClick={() => { closeAccount(); setHelpSettingsOpen(true); }} startIcon={<HelpOutline />} sx={accountRow}>
            {t('help.helpSettings')}
          </Button>
          <Button
            data-testid="logout-action"
            onClick={handleLogout}
            startIcon={<Logout />}
            sx={{ ...accountRow, color: 'error.main', mt: 0.5 }}
          >
            {t('layout.signOut')}
          </Button>
        </Box>
      </Popover>
      <HelpSettings open={helpSettingsOpen} onClose={() => setHelpSettingsOpen(false)} />
    </Box>
    </MobileTopBarActionsContext.Provider>
  );
};

export default Layout;
